import { useState, useRef, useEffect } from "react";
import { Typography, AppButton, InnoveraLoading, ConfirmationModal } from "@libs/zapi-ui/custom";
import { SlideCard } from "./SlideCard";
import { SlidesSidebar } from "./SlidesSidebar";
import { PdfPreview } from "./PdfPreview";
import { usePresentation } from "@/zapi-frontend/shared/hooks/usePresentation";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { InnoveraIcon } from "@libs/zapi-ui/assets";
import { TrashSimpleIcon } from "@phosphor-icons/react";
import { OutlineEmpty } from "./OutlineEmpty";
import { PreviewEmpty } from "./PreviewEmpty";
import { formatGeneratedTime } from "../lib/formatGeneratedTime";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { PresentationChat } from "./PresentationChat";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useSearchParams } from "react-router-dom";

type TViewMode = "outline" | "preview";

export function PresentationPage() {
  const {
    slides,
    isLoading,
    isSlidesGenerating,
    isGammaGenerating,
    isClearing,
    gammaPdfUrl,
    hasSlides,
    slidesGeneratedAt,
    allowRegeneration,
    updateSlide,
    deleteSlide,
    addSlide,
    reorderSlides,
    generateSlides,
    generateGammaPreview,
    clearPresentation,
    isSaving
  } = usePresentation();
  const { selectedProject, isFetchProjectLoading } = useProjectStore();
  const { isLabs } = useMeStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = searchParams.get("view") as TViewMode;

  const slidesContainerRef = useRef<HTMLDivElement>(null);
  const isProgrammaticScrollRef = useRef(false);
  const [outlineSlideIndex, setOutlineSlideIndex] = useState(0);
  const [previewPageIndex, setPreviewPageIndex] = useState(1);
  const [isClearModalOpen, setIsClearModalOpen] = useState(false);

  function selectSlideHandler(index: number) {
    isProgrammaticScrollRef.current = true;
    setOutlineSlideIndex(index);
    const slideElement = document.getElementById(`slide-${slides[index].id}`);
    if (slideElement) {
      slideElement.scrollIntoView({ behavior: "smooth", block: "start" });
      setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, 500);
    }
  }

  function handleSlidesScroll() {
    if (isProgrammaticScrollRef.current) return;
    if (slides.length === 0) return;

    const container = slidesContainerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const containerCenter = containerRect.top + containerRect.height / 2;

    let closestIndex = 0;
    let closestDistance = Infinity;

    slides.forEach((slide, index) => {
      const el = document.getElementById(`slide-${slide.id}`);
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const elCenter = rect.top + rect.height / 2;
      const distance = Math.abs(containerCenter - elCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    setOutlineSlideIndex(closestIndex);
  }

  function handleAddSlide() {
    const newSlideId = addSlide(outlineSlideIndex);
    const newIndex = outlineSlideIndex + 1;

    setTimeout(() => {
      setOutlineSlideIndex(newIndex);
      const slideElement = document.getElementById(`slide-${newSlideId}`);
      if (slideElement) {
        slideElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 50);
  }

  const isPageLoading = isLoading || isFetchProjectLoading;
  const isDisabledGeneratingSlides = isSlidesGenerating || !hasSlides || !allowRegeneration || isSaving;

  function switchToOutline() {
    setSearchParams({ tab: "presentation", view: "outline" }, { replace: true });
    setTimeout(() => {
      const slide = slides[outlineSlideIndex];
      if (!slide) return;

      const slideElement = document.getElementById(`slide-${slide.id}`);
      if (!slideElement) return;

      const container = slidesContainerRef.current;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const elementRect = slideElement.getBoundingClientRect();
      const isInViewport = elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom;

      if (!isInViewport) {
        isProgrammaticScrollRef.current = true;
        slideElement.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => {
          isProgrammaticScrollRef.current = false;
        }, 500);
      }
    }, 50);
  }

  function switchToPreview() {
    setSearchParams({ tab: "presentation", view: "preview" }, { replace: true });
  }

  useEffect(() => {
    if (!viewMode) setSearchParams({ tab: "presentation", view: "outline" }, { replace: true });
  }, [viewMode, setSearchParams]);

  return (
    <div className={cn("w-full h-[calc(100vh-56px)] flex p-2", viewMode === "preview" ? "" : "gap-2")}>
      <div className="w-full h-full flex flex-col bg-innoveraZinc-100">
        <div className="min-h-14 px-4 w-full bg-white flex justify-between items-center">
          <div className="flex h-8 items-center gap-2 bg-innoveraZinc-100 rounded-[8px] p-0.5">
            <button
              onClick={switchToOutline}
              data-testid="presentation-tab-outline"
              className={cn(
                "px-4 py-2 rounded-[6px] transition-all",
                viewMode === "outline" ? "bg-white" : "bg-transparent"
              )}
            >
              <Typography variant="label3" color="zinc900">
                Edit outline
              </Typography>
            </button>
            <button
              onClick={switchToPreview}
              data-testid="presentation-tab-preview"
              className={cn(
                "px-4 py-2 rounded-[6px] transition-all",
                viewMode === "preview" ? "bg-white" : "bg-transparent"
              )}
            >
              <Typography variant="label3" color="zinc900">
                Preview Slides
              </Typography>
            </button>
          </div>

          <div className="flex items-center gap-2">
            {slidesGeneratedAt && (
              <Typography variant="body3" color="zinc500">
                {formatGeneratedTime(slidesGeneratedAt)}
              </Typography>
            )}
            <AppButton
              onClick={generateGammaPreview}
              disabled={isDisabledGeneratingSlides}
              isLoading={isGammaGenerating}
              loadingText="Generating..."
              data-testid="presentation-generate-preview"
              startAdornment={
                <InnoveraIcon
                  width={16}
                  height={16}
                  className={cn(isDisabledGeneratingSlides ? "[&>path]:fill-innoveraZinc-400" : "[&>path]:fill-white")}
                />
              }
            >
              Generate Preview
            </AppButton>
            <AppButton
              onClick={() => setIsClearModalOpen(true)}
              disabled={isGammaGenerating || isSlidesGenerating || !hasSlides || isSaving}
              isLoading={isClearing}
              loadingText="Deleting..."
              appearance="outlined"
              color="zinc150"
              startAdornment={<TrashSimpleIcon size={16} />}
            >
              Clear Outline&Slides
            </AppButton>
            <ConfirmationModal
              isOpen={isClearModalOpen}
              onClose={() => setIsClearModalOpen(false)}
              onConfirm={clearPresentation}
              confirmText="Delete"
              title={`Are you sure you want to delete ${selectedProject?.name} presentation? `}
              description="Deleting this will let you create a new presentation from scratch."
            />
          </div>
        </div>

        <div className="h-[calc(100%-56px)] flex pt-2">
          {viewMode === "outline" && (
            <div className="flex w-full gap-2">
              <SlidesSidebar
                slides={slides}
                selectedIndex={outlineSlideIndex}
                isGenerating={isSlidesGenerating || isPageLoading}
                onSelectSlide={selectSlideHandler}
                onDeleteSlide={deleteSlide}
                onAddSlide={handleAddSlide}
                onReorderSlides={reorderSlides}
              />
              {isPageLoading && (
                <div className="w-full h-full flex flex-col items-center justify-center gap-4">
                  <InnoveraLoading size={90} />
                  <Typography variant="body1">Loading presentation outline...</Typography>
                </div>
              )}

              {!isPageLoading && (
                <>
                  {isSlidesGenerating && (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-4">
                      <InnoveraLoading size={90} />
                      <Typography variant="body1">Generating Presentation Outline...</Typography>
                    </div>
                  )}
                  {hasSlides && !isSlidesGenerating && (
                    <div
                      ref={slidesContainerRef}
                      onScroll={handleSlidesScroll}
                      className="w-full min-h-0 overflow-y-auto"
                    >
                      <div className="w-full grid gap-2 pb-4">
                        {slides.map((slide, index) => (
                          <SlideCard
                            key={slide.id}
                            slide={slide}
                            index={index}
                            selectedIndex={outlineSlideIndex}
                            onUpdate={updateSlide}
                            onDelete={deleteSlide}
                            onSelect={selectSlideHandler}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {!hasSlides && !isSlidesGenerating && <OutlineEmpty onGenerate={generateSlides} />}
                </>
              )}
            </div>
          )}

          {viewMode === "preview" && (
            <div className="flex w-full min-h-0 overflow-hidden justify-center">
              {isSlidesGenerating && !gammaPdfUrl && (
                <div className="w-full h-full flex flex-col items-center justify-center gap-4">
                  <InnoveraLoading size={90} />
                  <Typography variant="body1">Generating Presentation Outline...</Typography>
                </div>
              )}
              {isGammaGenerating && !isSlidesGenerating && (
                <div className="w-full h-full flex flex-col items-center justify-center gap-4">
                  <InnoveraLoading size={90} />
                  <Typography variant="body1">Generating Presentation View...</Typography>
                </div>
              )}
              {!isGammaGenerating && (
                <PdfPreview
                  pdfUrl={gammaPdfUrl}
                  slideTitles={slides.map((slide) => slide.title)}
                  initialPage={previewPageIndex}
                  onPageChange={setPreviewPageIndex}
                  isPageLoading={isPageLoading}
                />
              )}
              {!gammaPdfUrl && !isGammaGenerating && !isSlidesGenerating && !isPageLoading && (
                <PreviewEmpty
                  onGeneratePreview={generateGammaPreview}
                  onGenerateOutline={generateSlides}
                  hasSlides={hasSlides}
                  allowRegeneration={allowRegeneration}
                />
              )}
            </div>
          )}
        </div>
      </div>
      {viewMode !== "preview" && isLabs && <PresentationChat />}
    </div>
  );
}
